// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import type { SchemaModel } from '../../models/Schema'
import type { RecordModel } from '../../models/Record'
import type { MigrationHistoryEntry, MigrationRunnerOptions, SchemaMigrationResult } from './types'
import type { MigrationRegistry } from './registry'

/**
 * Structural interface for the schemas collection.
 * Accepts both RxDB collections and REST-compatible wrappers.
 */
interface SchemasCollection {
  find(query: { selector: Record<string, unknown> }): {
    exec(): Promise<SchemaModel[]>
  }
  update(id: string, patch: Partial<SchemaModel>): Promise<unknown>
}

/**
 * Structural interface for the records collection.
 */
interface RecordsCollection {
  find(query: { selector: Record<string, unknown> }): {
    exec(): Promise<RecordModel[]>
  }
  update(id: string, patch: Partial<RecordModel>): Promise<unknown>
}

/**
 * Determine the effective migration version for a schema.
 *
 * If the schema has `metadata.lastMigrationVersion`, use that value.
 * Otherwise fall back to the schema's own `version` field — this treats
 * schemas that predate the migration system as already at their declared
 * version, so no spurious re-migrations are triggered on first run.
 */
function getEffectiveVersion(schema: SchemaModel): number {
  const metadata = (schema as any).metadata as Record<string, unknown> | undefined
  if (metadata && typeof metadata.lastMigrationVersion === 'number') {
    return metadata.lastMigrationVersion
  }
  return schema.version
}

/**
 * Run all pending migrations across all orgs.
 *
 * Algorithm:
 * 1. Discover all builtIn schemas from the collection
 * 2. Extract the distinct set of orgIds across those schemas
 * 3. For each org, iterate every builtIn schema and compute pending migrations
 * 4. Apply schema-definition changes (field additions, renames, etc.)
 * 5. Optionally migrate existing record data via `migrateData` transforms
 * 6. Persist the updated schema (with audit history) unless dryRun is set
 *
 * Isolation contract: record write errors are captured per-record and do NOT
 * abort the migration for that schema. The schema definition is still updated
 * so subsequent runs can proceed cleanly. Callers should inspect
 * `result.errors` and re-drive failed records through compensating logic.
 *
 * @param registry     - The populated MigrationRegistry for this deployment
 * @param schemasCollection - Collection holding SchemaModel documents
 * @param recordsCollection - Collection holding RecordModel documents
 * @param options       - Optional runner controls (dryRun, filter, etc.)
 * @returns One result per (org, schema) pair that had pending migrations
 */
export async function runMigrations(
  registry: MigrationRegistry,
  schemasCollection: SchemasCollection,
  recordsCollection: RecordsCollection,
  options: MigrationRunnerOptions = {}
): Promise<SchemaMigrationResult[]> {
  const { dryRun = false, schemas: schemaFilter } = options
  const results: SchemaMigrationResult[] = []

  // 1. Discover all builtIn schemas — non-builtIn schemas are user-defined
  //    and must never be touched by the product migration runner.
  const allSchemas = await schemasCollection.find({ selector: { builtIn: true } }).exec()

  // 2. Get distinct orgIds so we process each tenant independently.
  const orgIds = [...new Set(allSchemas.map(s => s.orgId))]

  // 3. Iterate per org, then per schema within that org.
  for (const orgId of orgIds) {
    const orgSchemas = allSchemas.filter(s => s.orgId === orgId)

    for (const schema of orgSchemas) {
      // Apply the optional name filter supplied by the caller.
      if (schemaFilter && !schemaFilter.includes(schema.name)) continue

      const currentVersion = getEffectiveVersion(schema)
      const pending = registry.getPendingMigrations(schema.name, currentVersion)

      // Nothing to do for this schema in this org.
      if (pending.length === 0) continue

      const result: SchemaMigrationResult = {
        schema: schema.name,
        orgId,
        fromVersion: currentVersion,
        toVersion: pending[pending.length - 1].version,
        migrationsApplied: pending.length,
        recordsUpdated: 0,
        errors: [],
        dryRun,
      }

      // 4. Apply each migration's schema changes in ascending version order.
      //    We accumulate the transformed schema in memory so each migration
      //    sees the output of the previous one, not the original.
      let currentSchema = { ...schema }

      for (const migration of pending) {
        const changes = migration.schemaChanges(currentSchema)
        currentSchema = { ...currentSchema, ...changes }

        // 5. Migrate existing record data when the migration defines it.
        if (migration.migrateData) {
          const records = await recordsCollection.find({ selector: { schemaId: schema.id } }).exec()

          for (const record of records) {
            try {
              const transformed = migration.migrateData(record)
              if (!dryRun) {
                await recordsCollection.update(record.id, transformed)
              }
              result.recordsUpdated++
            } catch (err) {
              // Capture but do not rethrow — schema upgrade continues.
              result.errors.push({
                recordId: record.id,
                error: (err as Error).message,
              })
            }
          }
        }
      }

      // 6. Persist the updated schema definition with migration audit trail.
      if (!dryRun) {
        const existingMetadata = (currentSchema as any).metadata ?? {}
        const existingHistory: MigrationHistoryEntry[] =
          (existingMetadata.migrationHistory as MigrationHistoryEntry[]) ?? []

        const newHistory: MigrationHistoryEntry[] = [
          ...existingHistory,
          ...pending.map(m => ({
            version: m.version,
            appliedAt: new Date().toISOString(),
            description: m.description,
          })),
        ]

        await schemasCollection.update(schema.id, {
          ...currentSchema,
          metadata: {
            ...existingMetadata,
            lastMigrationVersion: result.toVersion,
            migrationHistory: newHistory,
          },
        } as any)
      }

      results.push(result)
    }
  }

  return results
}
